import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Alert, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import Settings from '../../../Components/Settings';
import FormInput from '../../../Components/Ui/FormInput';
import ButtonField from '../../../Components/Ui/ButtonField';
import ClientRepository from '../../../Repositories/ClientRepository';
import { useTailwindBrand } from '../../../Components/shared';
import { SHOW_ERROR, SHOW_SUCCESS } from '../../../Redux/Reducers/MessageReducers';
import { userClientSetAccountAction } from '../../../Redux/Actions/UserActions';
import { getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from '../../../config/environment.config';
import { useAiciOnboarding } from '../../../Hooks/useAiciOnboarding';
import { AiciOnboardingProvider } from '../../../Contexts/AiciOnboardingContexte';
import { AiciStep } from '../../../Hooks/InterfaceAiciOnboarding';
import OnboardingStepper from '../../AiciOnboarding/OnboardingStepper';
import PersonalInfoStep from '../../AiciOnboarding/Steps/PersonalInfoStep';
import BirthBankStep from '../../AiciOnboarding/Steps/BirthBankStep';
import AddressStep from '../../AiciOnboarding/Steps/AddressStep';
import RecapStep from '../../AiciOnboarding/Steps/RecapStep';
import ConfirmationStep from '../../AiciOnboarding/Steps/ConfirmationStep';

const STEPPER_LABELS = [
    'Vos informations',
    'Naissance & banque',
    'Votre adresse',
    'Récapitulatif',
    'Confirmation',
];

/**
 * Espace client : rubrique Avance immédiate des paramètres.
 * Trois situations :
 * 1. AICI déjà active, sans invitation : carte de confirmation.
 * 2. AICI déjà active + clé de contact (?contact=) : mise en relation seule
 *    avec le prof qui invite (favori + customFees), sans re-passer par l'URSSAF.
 * 3. AICI non activée : parcours d'activation prérempli depuis le compte,
 *    réutilisant les étapes du lien prof, avec un code d'invitation optionnel.
 */
const ClientAiciStep = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { classes, brandConfig } = useTailwindBrand();
    const client = useSelector((state: any) => state.user.currentUser?.client);

    // clé de contact portée par le lien d'invitation du prof
    const contactKey = useMemo(
        () => new URLSearchParams(location.search).get('contact') ?? undefined,
        [location.search]
    );

    const activated = !!client?.idClientTiersPrestation;

    // parcours d'activation (mode self) : monté seulement quand nécessaire
    const aici = useAiciOnboarding(activated ? undefined : contactKey, { self: true, client });
    const { teachr, currentStep, goToStep, invitationCode, setInvitationCode } = aici;

    const [linking, setLinking] = useState(false);
    const [linkedTeachr, setLinkedTeachr] = useState<string | null>(null);

    const handleLink = async () => {
        if (!contactKey || linking) return;
        setLinking(true);
        try {
            const result: any = await ClientRepository.aiciLink({ teachrContactKey: contactKey });
            const name = [result?.teachrFirstName, result?.teachrLastName].filter(Boolean).join(' ');
            setLinkedTeachr(name || '');
            dispatch({ type: SHOW_SUCCESS, payload: `La mise en relation est faite${name ? ` avec ${name}` : ''}. Votre ${getActivePrestataireBrandConfig()} peut maintenant déclarer vos ${getActivePrestationBrandConfig('s')}.` });
            try {
                const refreshed = await ClientRepository.getCurrentClient();
                if (refreshed) dispatch(userClientSetAccountAction(JSON.parse(JSON.stringify(refreshed))));
            } catch { /* non bloquant */ }
        } catch (error: any) {
            const translate = error?.data?.translate ?? error?.translate;
            dispatch({ type: SHOW_ERROR, payload: translate ?? 'La mise en relation a échoué. Veuillez réessayer.' });
        } finally {
            setLinking(false);
        }
    };

    const renderStepScreen = () => {
        switch (currentStep) {
            case AiciStep.PersonalInfo: return <PersonalInfoStep />;
            case AiciStep.BirthBank: return <BirthBankStep />;
            case AiciStep.Address: return <AddressStep />;
            case AiciStep.Recap: return <RecapStep />;
            case AiciStep.Confirmation: return <ConfirmationStep />;
            default: return <PersonalInfoStep />;
        }
    };

    return (
        <Settings
            title="⚡ Avance immédiate"
            subtitle="L'avance immédiate de crédit d'impôt URSSAF : ne payez que 50 % de vos prestations, le crédit d'impôt est déduit à la source."
        >
            <Grid item xs={12}>
                {activated ? (
                    <div className="space-y-4">
                        <div
                            className="flex items-start gap-3 rounded-xl p-4 border"
                            style={{
                                backgroundColor: `${brandConfig.colors?.primary ?? '#16a34a'}08`,
                                borderColor: `${brandConfig.colors?.primary ?? '#16a34a'}30`,
                            }}
                        >
                            <CheckCircleIcon sx={{ color: '#059669', mt: 0.25 }} />
                            <div className={classes.fontFamily}>
                                <p className={`font-extrabold ${classes.primaryText}`}>Votre avance immédiate est activée</p>
                                <p className="text-sm text-gray-600">
                                    Votre compte URSSAF tiers de prestation est en place : vos {getActivePrestationBrandConfig('s')} déclarés bénéficient de l'avance immédiate de crédit d'impôt.
                                </p>
                            </div>
                        </div>
                        {contactKey && linkedTeachr === null && (
                            <div className="rounded-xl p-4 border border-amber-200 bg-amber-50 space-y-3">
                                <p className={`font-bold text-amber-900 ${classes.fontFamily}`}>
                                    Un {getActivePrestataireBrandConfig()} souhaite être rattaché à votre compte
                                </p>
                                <p className={`text-sm text-amber-800 ${classes.fontFamily}`}>
                                    Votre avance immédiate étant déjà active, il ne reste qu'à accepter la mise en relation pour qu'il puisse déclarer vos {getActivePrestationBrandConfig('s')}.
                                </p>
                                <ButtonField
                                    label="Accepter la mise en relation"
                                    variant="contained"
                                    className="font-bold"
                                    onClick={handleLink}
                                    loading={linking}
                                    disabled={linking}
                                />
                            </div>
                        )}
                        {linkedTeachr !== null && (
                            <Alert severity="success" sx={{ fontFamily: brandConfig.fontFamily }}>
                                Mise en relation effectuée{linkedTeachr ? ` avec ${linkedTeachr}` : ''}.
                            </Alert>
                        )}
                    </div>
                ) : (
                    <AiciOnboardingProvider value={aici}>
                        <div className="space-y-4">
                            {teachr ? (
                                <Alert severity="info" icon={<BoltIcon />} sx={{ fontFamily: brandConfig.fontFamily }}>
                                    Votre {getActivePrestataireBrandConfig()} <strong className="capitalize">{teachr.firstName}</strong> vous invite à activer votre avance immédiate : vos informations sont préremplies, il ne reste que quelques champs à compléter.
                                </Alert>
                            ) : (
                                <Alert severity="info" icon={<BoltIcon />} sx={{ fontFamily: brandConfig.fontFamily }}>
                                    Complétez les informations manquantes pour créer votre compte URSSAF tiers de prestation. Vos informations existantes sont préremplies et restent modifiables.
                                </Alert>
                            )}

                            {/* Code d'invitation du prof (optionnel, activation autonome) */}
                            {!teachr && currentStep < AiciStep.Confirmation && (
                                <div className="lg:w-1/2">
                                    <FormInput
                                        label={`Code d'invitation de votre ${getActivePrestataireBrandConfig()} (optionnel)`}
                                        type="text"
                                        placeholder="Ex : ABDO1234"
                                        value={invitationCode ?? ''}
                                        onChange={(e) => setInvitationCode?.(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="flex flex-col gap-4 lg:flex-row">
                                <aside className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl lg:w-1/4 lg:order-last h-max">
                                    <OnboardingStepper
                                        steps={STEPPER_LABELS}
                                        activeStep={currentStep}
                                        onStepClick={currentStep < AiciStep.Confirmation ? goToStep : undefined}
                                    />
                                </aside>
                                <main className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl lg:w-3/4 lg:p-6">
                                    {renderStepScreen()}
                                </main>
                            </div>
                        </div>
                    </AiciOnboardingProvider>
                )}
            </Grid>
        </Settings>
    );
};

export default ClientAiciStep;
